import type { ReactNode } from "react";

import { trpc } from "@blis/trpc/react";

import { CommandMenu, CommandMenuProvider } from "@/components/CommandMenu";
import { DetailItem } from "@/components/DetailItem";
import { SubHeader } from "@/components/SubHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Description, Label } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/toast";
import { useAuth } from "../hooks";
import { useProfileSettings } from "../hooks/profile";

export function ProfilePage() {
  const { user } = useAuth();
  return (
    <>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb>Profile</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
      </SubHeader>

      <div className="flex flex-col p-8 gap-8 flex-1 overflow-auto">
        <Section
          title={
            <div className="flex items-center gap-2">
              <UserAvatar user={user?.profile} size="large" />
              {user?.profile.username}
            </div>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <DetailItem title="Username">{user?.profile.username}</DetailItem>
            <DetailItem title="Email">{user?.profile.email}</DetailItem>
            <DetailItem title="Organization">
              {user?.profile.org.name}
            </DetailItem>
            <DetailItem title="Role">{user?.profile.default_role}</DetailItem>
          </div>
        </Section>
        <Separator />
        <Settings />
      </div>
      <CommandMenuProvider>
        <CommandMenu />
      </CommandMenuProvider>
    </>
  );
}

function Settings() {
  const profile = useProfileSettings();
  const { data } = trpc.orgs.primaryOrgsList.useQuery();
  const clients = data || [];

  return (
    <Section title="Settings" className="max-w-3xl border rounded-lg p-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Label htmlFor="default-client">Default client</Label>
            <Description>
              Select this client automatically when submitting orders.
            </Description>
          </div>
          <Select
            id="default-client"
            className="w-72"
            placeholder="Select client"
            selectedKey={profile.state.defaultClient}
            onSelectionChange={(value) => {
              profile.setDefaultClient(value as number | null | undefined);
              if (!value) {
                toast.success("Default client cleared");
              } else {
                const client = clients.find((c) => c.id === value);
                if (client) {
                  toast.success("Default client updated", {
                    description: client.name,
                  });
                }
              }
            }}
          >
            <SelectTrigger isClearable />
            <SelectList items={clients}>
              {(client) => (
                <SelectItem id={client.id} textValue={client.name}>
                  {client.name}
                </SelectItem>
              )}
            </SelectList>
          </Select>
        </div>
      </div>
    </Section>
  );
}

function Section({
  title,
  children,
  className,
}: {
  title: ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Heading level={2} className="pb-4">
        {title}
      </Heading>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}
